import React, { useState } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent,
  IonGrid, IonRow, IonCol, IonCard, IonCardHeader, IonCardTitle,
  IonCardContent, IonList, IonSpinner, IonButton, IonItem, IonLabel,
  IonBadge, IonIcon,
} from '@ionic/react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Legend, LineChart, Line, CartesianGrid,
} from 'recharts';
import { arrowForward, add, swapHorizontal, remove, cash, walletOutline, trendingUp, trendingDown, settingsOutline } from 'ionicons/icons';
import { useDashboard } from '../hooks/useDashboard';
import { useSnapshots, useEvents } from '../hooks/useInventory';
import { useCashTransactions } from '../hooks/useCash';
import StatsCard from '../components/StatsCard';
import CardItem from '../components/CardItem';
import { useHistory } from 'react-router-dom';
import { InventoryEvent, CashTransaction } from '../types';

const COLORS = ['#3880ff', '#3dc2ff', '#5260ff', '#2dd36f', '#ffc409', '#eb445a', '#92949c', '#f4a261'];

const RANGE_OPTIONS = [
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '1y', days: 365 },
  { label: 'All', days: 365 },
];

const EVENT_ICONS: Record<string, string> = {
  add: add,
  trade: swapHorizontal,
  loss: remove,
  sale: cash,
};

const EVENT_COLORS: Record<string, string> = {
  add: 'success',
  trade: 'primary',
  loss: 'danger',
  sale: 'warning',
};

function formatEventDescription(event: InventoryEvent): string {
  switch (event.event_type) {
    case 'add':
      return `Added ${event.card_name}`;
    case 'trade':
      return `Traded ${event.card_name}${event.trade_in_card_name ? ` for ${event.trade_in_card_name}` : ''}`;
    case 'sale':
      return `Sold ${event.card_name}${event.sale_price ? ` for $${event.sale_price.toFixed(2)}` : ''}`;
    case 'loss':
      return `Lost ${event.card_name}`;
    default:
      return event.card_name;
  }
}

const Dashboard: React.FC = () => {
  const { data, isLoading } = useDashboard();
  const history = useHistory();
  const [rangeDays, setRangeDays] = useState(30);

  const { data: snapshots } = useSnapshots(rangeDays);
  const { data: recentEvents } = useEvents(1, 5);
  const { data: recentCashTxns } = useCashTransactions(1, 5);

  if (isLoading || !data) {
    return (
      <IonPage>
        <IonHeader><IonToolbar color="primary"><IonTitle>Dashboard</IonTitle></IonToolbar></IonHeader>
        <IonContent className="ion-padding ion-text-center"><IonSpinner /></IonContent>
      </IonPage>
    );
  }

  const chartData = (snapshots || []).map((s) => ({
    date: s.snapshot_date,
    value: s.total_value,
    cards: s.total_cards,
  }));

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="primary" style={{ position: 'relative' }}>
          <IonTitle>Dashboard</IonTitle>
          <IonIcon
            icon={settingsOutline}
            onClick={() => history.push('/settings')}
            style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', fontSize: 24, cursor: 'pointer', color: '#fff', zIndex: 10 }}
          />
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonGrid>
          <IonRow>
            <IonCol size="6" sizeMd="3">
              <StatsCard title="Total Cards" value={data.total_cards} />
            </IonCol>
            <IonCol size="6" sizeMd="3">
              <StatsCard title="Binders" value={data.total_binders} color="#ffc409" />
            </IonCol>
            <IonCol size="6" sizeMd="3">
              <StatsCard title="Total Value" value={`$${data.total_value.toFixed(2)}`} color="#2dd36f" />
            </IonCol>
            <IonCol size="6" sizeMd="3">
              <StatsCard title="Cash on Hand" value={`$${(data.cash_on_hand ?? 0).toFixed(2)}`} color="#eb445a" />
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol size="6" sizeMd="3">
              <StatsCard
                title="Trading Profit"
                value={`${(data.trading_profit ?? 0) >= 0 ? '+' : ''}$${(data.trading_profit ?? 0).toFixed(2)}`}
                color={(data.trading_profit ?? 0) >= 0 ? '#2dd36f' : '#eb445a'}
              />
            </IonCol>
            <IonCol size="6" sizeMd="3">
              <StatsCard
                title="Collection Gain"
                value={`${(data.collection_gain ?? 0) >= 0 ? '+' : ''}$${(data.collection_gain ?? 0).toFixed(2)}`}
                color={(data.collection_gain ?? 0) >= 0 ? '#2dd36f' : '#eb445a'}
              />
            </IonCol>
          </IonRow>
        </IonGrid>

        {/* Inventory Trend Chart */}
        <IonCard>
          <IonCardHeader>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <IonCardTitle>Inventory Trends</IonCardTitle>
              <div>
                {RANGE_OPTIONS.map((opt) => (
                  <IonButton
                    key={opt.label}
                    size="small"
                    fill={rangeDays === opt.days ? 'solid' : 'outline'}
                    onClick={() => setRangeDays(opt.days)}
                    style={{ '--padding-start': '8px', '--padding-end': '8px' } as any}
                  >
                    {opt.label}
                  </IonButton>
                ))}
              </div>
            </div>
          </IonCardHeader>
          <IonCardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(d) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  />
                  <YAxis yAxisId="value" orientation="left" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
                  <YAxis yAxisId="cards" orientation="right" tick={{ fontSize: 10 }} />
                  <Tooltip
                    labelFormatter={(d) => new Date(d).toLocaleDateString()}
                    formatter={(v, name) => {
                      const num = Number(v ?? 0);
                      return name === 'Value' ? `$${num.toFixed(2)}` : num;
                    }}
                  />
                  <Legend />
                  <Line yAxisId="value" type="monotone" dataKey="value" stroke="#2dd36f" name="Value" dot={false} />
                  <Line yAxisId="cards" type="monotone" dataKey="cards" stroke="#3880ff" name="Cards" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p>No trend data yet. Data will appear as snapshots are recorded daily.</p>
            )}
          </IonCardContent>
        </IonCard>

        <IonGrid>
          <IonRow>
            <IonCol size="12" sizeMd="6">
              <IonCard>
                <IonCardHeader><IonCardTitle>Value by Game</IonCardTitle></IonCardHeader>
                <IonCardContent>
                  {data.by_game.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={data.by_game} dataKey="value" nameKey="game_name" cx="50%" cy="50%" outerRadius={80} label>
                          {data.by_game.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => `$${Number(v).toFixed(2)}`} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p>No data yet. Add some cards!</p>
                  )}
                </IonCardContent>
              </IonCard>
            </IonCol>
            <IonCol size="12" sizeMd="6">
              <IonCard>
                <IonCardHeader><IonCardTitle>Cards by Rarity</IonCardTitle></IonCardHeader>
                <IonCardContent>
                  {data.by_rarity.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={data.by_rarity}>
                        <XAxis dataKey="rarity" tick={{ fontSize: 10 }} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#5260ff" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p>No data yet. Add some cards!</p>
                  )}
                </IonCardContent>
              </IonCard>
            </IonCol>
          </IonRow>
        </IonGrid>

        {/* Recent Activity */}
        <IonCard>
          <IonCardHeader>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <IonCardTitle>Recent Activity</IonCardTitle>
              <IonButton size="small" fill="clear" onClick={() => history.push('/history')}>
                View All <IonIcon icon={arrowForward} slot="end" />
              </IonButton>
            </div>
          </IonCardHeader>
          <IonCardContent style={{ padding: 0 }}>
            <IonList>
              {(() => {
                const eventItems = (recentEvents?.items || []).map((e) => ({
                  kind: 'event' as const,
                  id: `event-${e.id}`,
                  date: e.created_at!,
                  event: e,
                }));
                const cashItems = (recentCashTxns?.items || [])
                  .filter((t) => t.transaction_type !== 'sale')
                  .map((t) => ({
                    kind: 'cash' as const,
                    id: `cash-${t.id}`,
                    date: t.created_at!,
                    txn: t,
                  }));
                const merged = [...eventItems, ...cashItems]
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .slice(0, 5);

                if (merged.length === 0) {
                  return <div className="ion-padding ion-text-center">No activity yet.</div>;
                }

                return merged.map((item) => {
                  if (item.kind === 'event') {
                    const event = item.event;
                    return (
                      <IonItem key={item.id}>
                        <IonIcon icon={EVENT_ICONS[event.event_type] || add} slot="start" />
                        <IonLabel>
                          <h3>{formatEventDescription(event)}</h3>
                          <p>{event.game_name} &middot; {new Date(event.created_at!).toLocaleDateString()}</p>
                        </IonLabel>
                        <IonBadge color={EVENT_COLORS[event.event_type] || 'medium'} slot="end">
                          {event.event_type}
                        </IonBadge>
                      </IonItem>
                    );
                  } else {
                    const txn = item.txn;
                    return (
                      <IonItem key={item.id}>
                        <IonIcon icon={walletOutline} slot="start" />
                        <IonLabel>
                          <h3>
                            {txn.transaction_type === 'deposit' ? 'Deposited' : 'Withdrew'}{' '}
                            ${txn.amount.toFixed(2)}
                          </h3>
                          <p>
                            {new Date(txn.created_at!).toLocaleDateString()}
                            {txn.notes ? ` - ${txn.notes}` : ''}
                          </p>
                        </IonLabel>
                        <IonBadge color={txn.transaction_type === 'deposit' ? 'success' : 'danger'} slot="end">
                          {txn.transaction_type}
                        </IonBadge>
                      </IonItem>
                    );
                  }
                });
              })()}
            </IonList>
          </IonCardContent>
        </IonCard>

        <IonCard>
          <IonCardHeader><IonCardTitle>Recent Cards</IonCardTitle></IonCardHeader>
          <IonCardContent style={{ padding: 0 }}>
            <IonList>
              {data.recent_cards.length > 0 ? (
                data.recent_cards.map((card) => (
                  <CardItem key={card.id} card={card} onClick={() => history.push(`/cards/${card.id}`)} />
                ))
              ) : (
                <div className="ion-padding ion-text-center">No cards yet. Start adding some!</div>
              )}
            </IonList>
          </IonCardContent>
        </IonCard>
      </IonContent>
    </IonPage>
  );
};

export default Dashboard;
